import type { CustomChartSize } from '@/entities/dashboards';
import { chartGridRows, findChartGridColumn } from '../ui/DashboardPresetGrid/chartLayout';
import type { PresetChartFormValues } from './dashboardPresetForm';

/**
 * Место, куда встаёт перетаскиваемая диаграмма, — в понятиях состава: порядок, начало строки и
 * колонка строки.
 *
 * - `before` — перед колонкой диаграммы `chartId` в её строке (`chartId` — верхняя диаграмма
 *   колонки); перед первой колонкой строки перетаскиваемая открывает эту строку собой, а прежняя
 *   первая идёт следом за ней;
 * - `after` — следом за колонкой диаграммы `chartId` в её строке (`chartId` — нижняя диаграмма
 *   колонки);
 * - `above` / `below` — над диаграммой `chartId` или под ней, в её колонку строки и её шириной;
 * - `newRow` — новой строкой сразу за строкой, последняя диаграмма которой — `afterChartId`; строка,
 *   стоявшая ниже, остаётся отдельной строкой;
 * - `firstRow` — новой первой строкой: прежняя первая строка целиком встаёт под ней.
 */
export type ChartDropPlace =
  | { kind: 'firstRow' }
  | { kind: 'before'; chartId: string }
  | { kind: 'after'; chartId: string }
  | { kind: 'above'; chartId: string }
  | { kind: 'below'; chartId: string }
  | { kind: 'newRow'; afterChartId: string };

/**
 * Состав без диаграммы с позицией `index`. Верхняя диаграмма колонки уступает своё место следующей
 * диаграмме колонки: та встаёт во главу колонки с её началом строки, и раскладка вокруг не меняется.
 * Колонка из одной оставшейся диаграммы — обычная диаграмма строки.
 */
const detachChart = (charts: PresetChartFormValues[], index: number): PresetChartFormValues[] => {
  const detached = charts[index];
  const rest = charts.filter((_, position) => position !== index);
  const next = charts[index + 1];
  if (!detached.stacksBelow && next?.stacksBelow) {
    rest[index] = { ...next, stacksBelow: false, startsRow: detached.startsRow };
  }
  return rest;
};

/** Состав без диаграммы `chartId` — колонка, из которой её убрали, сохраняет свою раскладку. */
export const removeChartFromLayout = (charts: PresetChartFormValues[], chartId: string): PresetChartFormValues[] => {
  const index = charts.findIndex((chart) => chart.chartId === chartId);
  return index < 0 ? charts : detachChart(charts, index);
};

/**
 * Состав с долей ширины `size` у диаграммы `chartId`: у диаграммы колонки ширина одна на всю
 * колонку, поэтому её получают все диаграммы колонки. Та же ширина — тот же состав.
 */
export const resizeChartColumn = (
  charts: PresetChartFormValues[],
  chartId: string,
  size: CustomChartSize,
): PresetChartFormValues[] => {
  const index = charts.findIndex((chart) => chart.chartId === chartId);
  if (index < 0) return charts;
  const columnIndexes = new Set(findChartGridColumn(chartGridRows(charts), index)?.chartIndexes ?? [index]);
  if ([...columnIndexes].every((position) => charts[position].size === size)) return charts;
  return charts.map((chart, position) => (columnIndexes.has(position) ? { ...chart, size } : chart));
};

/**
 * Состав дашборда с диаграммой `sourceId`, перенесённой на место `place`.
 *
 * Место отсчитывается от раскладки, какой её видел пользователь: первая диаграмма строки узнаётся по
 * составу до переноса. Признак начала строки получает только то, что строку открывает, — первой
 * диаграмме состава он не нужен: строку она открывает и без него, а записанный признак делал бы
 * дашборд правленым там, где ничего не изменилось. Диаграмма, вставшая в колонку, принимает её
 * ширину; вынесенная из колонки — встаёт, куда отпущена, своей шириной.
 */
export const applyChartDrop = (
  charts: PresetChartFormValues[],
  sourceId: string,
  place: ChartDropPlace,
): PresetChartFormValues[] => {
  const sourceIndex = charts.findIndex((chart) => chart.chartId === sourceId);
  if (sourceIndex < 0) return charts;
  const source = charts[sourceIndex];

  const rest = detachChart(charts, sourceIndex);
  const rows = chartGridRows(charts);

  if (place.kind === 'firstRow') {
    // Диаграмма и так стоит в первой строке одна
    const firstRow = rows[0]?.chartIndexes ?? [];
    if (rest.length === 0 || (firstRow.length === 1 && firstRow[0] === sourceIndex)) return charts;
    // Прежняя первая диаграмма открывает строку под перенесённой — с колонками строки, как они стояли
    return [
      { ...source, startsRow: false, stacksBelow: false },
      { ...rest[0], startsRow: true, stacksBelow: false },
      ...rest.slice(1),
    ];
  }

  const anchorId = place.kind === 'newRow' ? place.afterChartId : place.chartId;
  const anchorIndex = rest.findIndex((chart) => chart.chartId === anchorId);
  if (anchorIndex < 0) return charts;

  const rowHeads = new Set(rows.map((row) => charts[row.chartIndexes[0]].chartId));
  const isSourceRowHead = rowHeads.has(sourceId);
  // Диаграмма стоит в строке одна в своей колонке: только такая может оказаться на прежнем месте
  const isSourceAlone = !source.stacksBelow && !charts[sourceIndex + 1]?.stacksBelow;
  const previousId = charts[sourceIndex - 1]?.chartId;
  const nextId = charts[sourceIndex + 1]?.chartId;
  // Строку, которую открывала перенесённая во главе колонки, дальше открывает следующая диаграмма колонки
  if (isSourceRowHead && !isSourceAlone && nextId) rowHeads.add(nextId);

  const placed = (index: number, startsRow: boolean): PresetChartFormValues[] => [
    ...rest.slice(0, index),
    { ...source, startsRow: startsRow && index > 0, stacksBelow: false },
    ...rest.slice(index),
  ];

  // Ширина колонки, в которую встаёт перенесённая, — по раскладке до переноса
  const anchorColumnSize = (): CustomChartSize => {
    const index = charts.findIndex((chart) => chart.chartId === anchorId);
    return findChartGridColumn(rows, index)?.size ?? rest[anchorIndex].size;
  };

  switch (place.kind) {
    case 'before': {
      if (!rowHeads.has(anchorId)) {
        // Диаграмма и так стоит перед соседкой своей строки — место то же, что было
        return isSourceAlone && nextId === anchorId ? charts : placed(anchorIndex, false);
      }
      // Прежняя первая диаграмма строки идёт следом за перенесённой, а не открывает строку сама
      const reordered = placed(anchorIndex, true);
      reordered[anchorIndex + 1] = { ...reordered[anchorIndex + 1], startsRow: false };
      return reordered;
    }
    case 'after':
      return placed(anchorIndex + 1, false);
    case 'below': {
      const reordered = placed(anchorIndex + 1, false);
      reordered[anchorIndex + 1] = { ...reordered[anchorIndex + 1], stacksBelow: true, size: anchorColumnSize() };
      return reordered;
    }
    case 'above': {
      const size = anchorColumnSize();
      const anchor = rest[anchorIndex];
      const reordered = placed(anchorIndex, false);
      if (anchor.stacksBelow) {
        reordered[anchorIndex] = { ...reordered[anchorIndex], stacksBelow: true, size };
        return reordered;
      }
      // Над верхней диаграммой колонки перенесённая встаёт во главу колонки — с её началом строки
      reordered[anchorIndex] = { ...reordered[anchorIndex], startsRow: anchor.startsRow, size };
      reordered[anchorIndex + 1] = { ...anchor, startsRow: false, stacksBelow: true };
      return reordered;
    }
    case 'newRow': {
      // Диаграмма и так открывает строку сразу за этой
      if (previousId === anchorId && isSourceRowHead && isSourceAlone) return charts;
      const reordered = placed(anchorIndex + 1, true);
      // Строка ниже не подтягивается к новой: место показано отдельной строкой между ними
      const nextIndex = anchorIndex + 2;
      if (nextIndex < reordered.length) {
        reordered[nextIndex] = { ...reordered[nextIndex], startsRow: true };
      }
      return reordered;
    }
  }
};
